import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { fmtDuration } from "../lib/duration";
import { toast } from "sonner";
import { X, Play, Warning } from "@phosphor-icons/react";

const LATE_REASONS = [
    "Vessel arrived late",
    "Berth not available",
    "Documentation / clearance",
    "Equipment readiness",
    "Surveyor / draft survey",
    "Weather",
    "Stockpile not ready",
    "Other",
];

function fmtDT(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    return d.toLocaleString(undefined, { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false });
}

export default function StartVesselModal({ item, onClose, onStarted }) {
    const planned = item?.start_datetime ? new Date(item.start_datetime) : new Date();
    const now = new Date();
    const lateMin = useMemo(() => Math.round((now.getTime() - planned.getTime()) / 60000), [planned, now]);
    const isLate = lateMin > 15;
    const earlyByMin = lateMin < -15 ? Math.abs(lateMin) : 0;

    const [reason, setReason] = useState(LATE_REASONS[0]);
    const [details, setDetails] = useState("");
    const [hatchNo, setHatchNo] = useState("1");
    const [loading, setLoading] = useState(false);

    // Conveyor routes available for this vessel's division — supervisor picks
    // which one(s) will be active when the vessel starts loading.
    const division = item?.division || "RBTG";
    const [routes, setRoutes] = useState([]);
    const [selectedRouteIds, setSelectedRouteIds] = useState([]);
    useEffect(() => {
        let cancelled = false;
        api.get("/conveyor-routes", { params: { division } })
            .then((r) => { if (!cancelled) setRoutes(r.data || []); })
            .catch(() => { if (!cancelled) setRoutes([]); });
        return () => { cancelled = true; };
    }, [division]);

    const toggleRoute = (id) => {
        setSelectedRouteIds((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );
    };

    const submit = async (e) => {
        e.preventDefault();
        if (isLate && !reason) {
            toast.error("Select a reason for the late start");
            return;
        }
        const hn = parseInt(hatchNo);
        if (!hn || hn < 1 || hn > 20) {
            toast.error("Enter a starting hatch number (1–20)");
            return;
        }
        if (routes.length > 0 && selectedRouteIds.length === 0) {
            toast.error("Select at least one conveyor route to activate at start");
            return;
        }
        setLoading(true);
        try {
            const payload = { hatch_no: hn, conveyor_route_ids: selectedRouteIds };
            if (isLate) {
                payload.late_reason = reason;
                payload.late_reason_details = details || null;
            }
            const { data } = await api.post(`/lineup/${item.id}/start`, payload);
            toast.success(`${data.vessel_name} is now loading on hatch ${hn}`);
            onStarted?.();
            onClose?.();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="start-vessel-modal">
            <div className="g-card w-full max-w-md overflow-hidden">
                <div className="g-card-header-dark" style={{ background: "var(--grindrod-green)" }}>
                    <Play size={18} weight="fill" />
                    <div className="font-display font-bold uppercase tracking-wide flex-1">Start vessel</div>
                    <button onClick={onClose} className="text-white/80 hover:text-white" data-testid="start-modal-close">
                        <X size={18} />
                    </button>
                </div>
                <form onSubmit={submit} className="p-5 space-y-4">
                    <div className="rounded-sm bg-slate-50 border border-slate-200 px-3 py-2.5 text-[12px] text-slate-700 space-y-1">
                        <div className="flex justify-between">
                            <span className="uppercase-label text-slate-500">Vessel</span>
                            <span className="font-display font-bold text-[var(--grindrod-navy)] uppercase">{item?.vessel_name}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="uppercase-label text-slate-500">Client</span>
                            <span className="tabular">{item?.client_name}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="uppercase-label text-slate-500">Product</span>
                            <span>{item?.product || "—"}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="uppercase-label text-slate-500">Forecast</span>
                            <span className="tabular">{Number(item?.tonnage || 0).toLocaleString()} t</span>
                        </div>
                        <div className="border-t border-slate-200 pt-1.5 mt-1.5 flex justify-between">
                            <span className="uppercase-label text-slate-500">Planned start</span>
                            <span className="tabular font-semibold">{fmtDT(item?.start_datetime)}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="uppercase-label text-slate-500">Actual start</span>
                            <span className="tabular font-semibold text-[var(--grindrod-navy)]">Now ({fmtDT(now.toISOString())})</span>
                        </div>
                    </div>

                    {isLate && (
                        <div className="border-l-4 border-[var(--grindrod-red)] bg-red-50 px-3 py-2 rounded-sm">
                            <div className="flex items-center gap-1.5 uppercase-label text-[var(--grindrod-red)]">
                                <Warning size={12} weight="fill" /> Late start — {fmtDuration(lateMin)} after plan
                            </div>
                            <div className="text-[11px] text-slate-600 mt-0.5">
                                A "Late start" delay record will be created on this vessel. Please capture the reason.
                            </div>
                        </div>
                    )}
                    {earlyByMin > 0 && (
                        <div className="border-l-4 border-[var(--grindrod-green)] bg-green-50 px-3 py-2 rounded-sm text-[11px] text-slate-600">
                            Starting <span className="font-semibold">{fmtDuration(earlyByMin)} early</span> — no delay will be logged.
                        </div>
                    )}

                    {isLate && (
                        <>
                            <div>
                                <label className="uppercase-label block mb-1.5">Reason for late start <span className="text-[var(--grindrod-red)]">*</span></label>
                                <select value={reason} onChange={(e) => setReason(e.target.value)} className="g-input" data-testid="start-late-reason">
                                    {LATE_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="uppercase-label block mb-1.5">Details <span className="text-slate-400 normal-case lowercase tracking-normal">(optional)</span></label>
                                <textarea
                                    value={details}
                                    onChange={(e) => setDetails(e.target.value)}
                                    rows={2}
                                    className="g-input"
                                    placeholder="e.g. Pilot delayed by 45 min due to fog"
                                    data-testid="start-late-details"
                                />
                            </div>
                        </>
                    )}

                    <div>
                        <label className="uppercase-label block mb-1.5">Starting hatch <span className="text-[var(--grindrod-red)]">*</span></label>
                        <div className="flex items-center gap-2">
                            <span className="uppercase-label text-slate-500 text-[10px] tabular w-4 text-center">H</span>
                            <input
                                type="number"
                                min="1"
                                max="20"
                                value={hatchNo}
                                onChange={(e) => setHatchNo(e.target.value)}
                                className="g-input tabular w-24"
                                data-testid="start-hatch-no"
                                required
                            />
                            <span className="text-[11px] text-slate-500">Loading will begin on this hatch. Use the "Hatch change" button on the live page when moving hatches.</span>
                        </div>
                    </div>

                    <div data-testid="start-route-picker">
                        <label className="uppercase-label block mb-1.5">
                            Active conveyor route(s) <span className="text-[var(--grindrod-red)]">*</span>
                            <span className="text-slate-400 normal-case lowercase tracking-normal ml-1">({division})</span>
                        </label>
                        {routes.length === 0 ? (
                            <div className="text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded-sm px-3 py-2">
                                No conveyor routes registered for {division} yet — an admin can add them from the Conveyor Systems tab.
                            </div>
                        ) : (
                            <>
                                <div className="grid gap-1.5" data-testid="start-route-options">
                                    {routes.map((r) => {
                                        const on = selectedRouteIds.includes(r.id);
                                        return (
                                            <button
                                                type="button"
                                                key={r.id}
                                                onClick={() => toggleRoute(r.id)}
                                                className={`text-left rounded-sm border px-3 py-2 transition-colors ${on ? "border-[var(--grindrod-navy)] bg-[var(--grindrod-navy)] text-white" : "border-slate-300 bg-white text-slate-700 hover:border-[var(--grindrod-navy)]"}`}
                                                data-testid={`start-route-option-${r.name.replace(/\s+/g, "-")}`}
                                            >
                                                <div className="flex items-center gap-2">
                                                    <span className={`inline-flex items-center justify-center w-4 h-4 rounded-sm border text-[10px] font-bold ${on ? "bg-white text-[var(--grindrod-navy)] border-white" : "bg-white border-slate-400 text-slate-400"}`}>
                                                        {on ? "✓" : ""}
                                                    </span>
                                                    <span className="font-display font-bold uppercase tracking-wide text-[12px] tabular">{r.name}</span>
                                                    {r.description && (
                                                        <span className={`text-[11px] ${on ? "text-white/80" : "text-slate-500"}`}>— {r.description}</span>
                                                    )}
                                                </div>
                                                <div className={`flex flex-wrap gap-1 mt-1 ${on ? "" : ""}`}>
                                                    {r.conveyors.map((c) => (
                                                        <span key={c} className={`px-1.5 py-0.5 text-[10px] rounded-sm tabular ${on ? "bg-white/15 border border-white/30 text-white" : "bg-slate-100 border border-slate-300 text-slate-700"}`}>
                                                            {c}
                                                        </span>
                                                    ))}
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                                <div className="text-[11px] text-slate-500 mt-1.5">
                                    Select one or more — multiple routes can run simultaneously. You can add or change routes from the dashboard later.
                                </div>
                            </>
                        )}
                    </div>

                    <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                        <button type="button" onClick={onClose} className="btn-outline" data-testid="start-cancel">Cancel</button>
                        <button type="submit" className="btn-primary" style={{ background: "var(--grindrod-green)" }} disabled={loading} data-testid="start-confirm">
                            {loading ? "Starting…" : "Start now"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
